import React, { ReactElement, useCallback, useMemo } from 'react';
import { Checkbox } from '../Checkbox/Checkbox';
import { Typography } from '../Typography/Typography';
import { Paper } from '../Paper/Paper';
import { styled } from '../../styles';
import { TransferListOption } from './TransferList.types';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { VirtualList } from '../VirtualList/VirtualList';
import {
  DEFAULT_ITEM_SIZE,
  TransferListCheckboxListItem,
  TransferListCheckboxListItemDraggable,
  TransferListCheckboxListItemDraggableBase
} from './TransferListCheckboxListItem';
import { DragAndDrop, DragAndDropProps } from '../DragAndDrop';
import { mergeClassNames } from '../../utils';
import { Dialog } from '../Dialog/Dialog';
import { Input } from '../Form/Input';
import { Button } from '../Button/Button';
import { Box } from '../Box/Box';

export interface TransferListCheckboxListProps<VALUE extends string>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onDragEnd'> {
  options: TransferListOption<VALUE>[];
  optionsWithOriginalNames?: TransferListOption<VALUE>[];
  checked: TransferListOption<VALUE>[];
  direction: 'right' | 'left';
  disabled: boolean;
  onCheck(option: TransferListOption<VALUE>): void;
  onLabelChange?(option: TransferListOption<VALUE>): void;
  checkAll(): void;
  searchValue: string;
  dataTest: string;
  disabledAll: boolean;
  error?: boolean;
  itemSize?: number;
  onDragEnd?: DragAndDropProps['onDragEnd'];
  slotProps?: {
    dragAndDrop?: Partial<Omit<DragAndDropProps, 'onDragEnd'>>;
  };
}

export const TransferListCheckboxList = <VALUE extends string>({
  disabled,
  options,
  optionsWithOriginalNames,
  direction,
  checked,
  onCheck,
  onLabelChange,
  searchValue,
  disabledAll,
  dataTest,
  checkAll,
  error,
  onDragEnd,
  itemSize = DEFAULT_ITEM_SIZE,
  slotProps,
  ...divElementProps
}: TransferListCheckboxListProps<VALUE>): ReactElement | null => {
  const { intl, format } = useIntlBds();
  const [isDragging, setIsDragging] = React.useState(false);
  const [optionToEdit, setOptionToEdit] = React.useState<TransferListOption<VALUE> | undefined>();
  const [nextLabel, setNextLabel] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const originalName = optionsWithOriginalNames?.find((it) => it.value === optionToEdit?.value)?.label;

  const handleLabelChange = useCallback((option: TransferListOption<VALUE>) => {
    setOpen(true);
    setNextLabel(option.label);
    setOptionToEdit(option);
  }, []);

  const optionsWithCallbacks = useMemo(() => {
    return {
      items: options,
      props: {
        itemsWithOriginalNames: optionsWithOriginalNames,
        checked,
        onCheck,
        onLabelChange: onLabelChange ? handleLabelChange : undefined,
        disabledAll,
        searchValue
      }
    };
  }, [options, optionsWithOriginalNames, checked, onCheck, disabledAll, searchValue, handleLabelChange, onLabelChange]);

  const handleDragEnd: DragAndDropProps['onDragEnd'] = (result, provided) => {
    onDragEnd?.(result, provided);
    setIsDragging(false);
  };

  return (
    <StyledPaper dataTest={dataTest} $error={error} {...divElementProps}>
      <div className="list-header">
        <Checkbox
          dataTest={`${dataTest}-check-all`}
          disabled={disabled}
          checked={options.length > 0 && options.length === checked.length}
          onChange={checkAll}
        >
          <Typography variant="body1">
            {direction === 'left'
              ? intl.TransferList.CheckboxList.availableFields
              : intl.TransferList.CheckboxList.chosenFields}
          </Typography>
          <Typography variant="body2">
            {format(intl.TransferList.CheckboxList.selected, {
              checkedLength: checked.length?.toString() ?? '0',
              totalLength: options.length?.toString() ?? '0'
            })}
          </Typography>
        </Checkbox>
      </div>
      <div
        className={mergeClassNames(`list-options`, {
          'is-dragging': isDragging
        })}
      >
        {!!onDragEnd && !!optionsWithCallbacks.items.length && !disabledAll ? (
          <DragAndDrop
            dataTest={dataTest}
            droppableId={dataTest}
            onDragEnd={handleDragEnd}
            mode="virtual"
            {...slotProps?.dragAndDrop}
            slotProps={{
              dragDropContext: {
                onDragStart() {
                  setIsDragging(true);
                }
              },
              droppable: {
                renderClone: (provided, snapshot, rubric) => {
                  const index = rubric.source.index;
                  const item = optionsWithCallbacks.items[index];
                  return (
                    <TransferListCheckboxListItemDraggableBase
                      style={provided.draggableProps.style ?? {}}
                      props={{
                        checked,
                        onCheck,
                        onLabelChange: onLabelChange ? handleLabelChange : undefined,
                        disabledAll,
                        searchValue
                      }}
                      dragging
                      item={item}
                      index={index}
                      ref={provided.innerRef}
                      key={item?.value ?? `${index}`}
                      draggableProps={provided.draggableProps}
                      dragHandleProps={provided.dragHandleProps}
                      snapshot={snapshot}
                    />
                  );
                }
              }
            }}
            getChildren={(droppableProvided, snapshot) => {
              return (
                <VirtualList<TransferListOption<VALUE>, InternalItemProps<VALUE>>
                  data={optionsWithCallbacks}
                  component={TransferListCheckboxListItemDraggable}
                  className={snapshot.isDraggingOver ? `is-dragging-over` : undefined}
                  itemSize={itemSize}
                  itemKey={(index, data) => data.items[index]?.value ?? `${index}`}
                  outerRef={droppableProvided.innerRef}
                />
              );
            }}
          />
        ) : (
          <VirtualList<TransferListOption<VALUE>, InternalItemProps<VALUE>>
            data={optionsWithCallbacks}
            component={TransferListCheckboxListItem}
            itemSize={itemSize}
            itemKey={(index, data) => data.items[index]?.value ?? `${index}`}
          />
        )}
      </div>
      {!!onLabelChange && (
        <Dialog
          open={open}
          title={intl.TransferList.DialogEditLabel.title}
          onClose={() => setOpen(false)}
          onSubmit={() => {
            if (optionToEdit) {
              onLabelChange({
                ...optionToEdit,
                label: nextLabel
              });
            }
            setOpen(false);
          }}
          show={{
            submitButton: true,
            cancelButton: true,
            closeButton: true
          }}
        >
          <Input
            label={intl.TransferList.DialogEditLabel.name}
            fullWidth
            value={nextLabel}
            onChange={(e) => setNextLabel(e.target.value)}
          />
          {originalName !== optionToEdit?.label && (
            <Box paddingTop={2}>
              <Typography variant="body" small color="colorOnSurfaceVariant" display="inline">
                {intl.TransferList.DialogEditLabel.originalName}: "{originalName}"{' '}
              </Typography>
              <Button size="small" color="primary" onClick={() => setNextLabel(originalName ?? '')}>
                {intl.TransferList.DialogEditLabel.useOriginalName}
              </Button>
            </Box>
          )}
        </Dialog>
      )}
    </StyledPaper>
  );
};

interface InternalItemProps<T extends string> {
  checked: TransferListOption<T>[];
  disabledAll?: boolean;
  searchValue: string;
  onCheck: (option: TransferListOption<T>) => void;
  onLabelChange?: (option: TransferListOption<T>) => void;
}

const StyledPaper = styled(Paper)<{ $error?: boolean }>`
  && {
    height: 100%;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: max-content 1fr;
    outline: ${({ $error, theme }) => ($error ? `1px solid ${theme.tokens.colorAlert} !important` : undefined)};

    .list-header {
      background-color: ${({ theme }) => theme.tokens.colorSurface};
      color: ${({ theme }) => theme.tokens.colorOnSurface};
      border-bottom: 1px solid ${({ theme }) => theme.tokens.colorSurfaceVariant};
    }
    .list-options {
      
      &.is-dragging [data-rbd-draggable-id] {
        pointer-events: none;
      }

      ul {
        padding: 0;
        margin: 0;
      }
    }
  }
`;
